import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDragLayer } from 'react-dnd';
import { Flex, Page, Typo } from '../../../ui';
import PageSection from '../../../ui/Page/PageSection';
import DraggableRow from './DraggableRow';
import { VerticalDropZone } from './DropZone';
import Module from '../../Module';
import ModuleWrapper from './ModuleWrapper';
import ModuleInsertionZone from './ModuleInsertionZone';
import { CONTENT_STANDARD } from '../../../utils/media';
import { useCourseSectionById, useCoursePageById } from '../../viewCourse/hooks';
import { useCourseTheme } from '../../course/hooks';
import { getUserThemeColor } from '../../../ui/theme/colors';
import { Breadcrumbs } from '@mui/material';
import { useMoveModuleToNewRowMutation, useUpdatePageModuleMutation } from '../editCourseApi';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { TOAST_SUCCESS, TOAST_ERROR } from '../../../utils/constants';

/**
 * DraggableCoursePage - Enhanced CoursePage with drag-and-drop layout editing
 * Uses the same Page component as viewCourse for WYSIWYG consistency
 *
 * @param {Object} props
 * @param {string} props.pageId - The page ID
 * @param {Object} props.pageInfo - Page data with pageRows
 * @param {Function} props.onEditModuleContent - Callback for editing module content
 * @param {Function} props.onEditModuleSettings - Callback for editing module settings
 * @param {Function} props.onDeleteModule - Callback for deleting a module
 * @param {Function} props.onInsertModule - Callback for inserting a new module
 */
const DraggableCoursePage = ({ pageId, pageInfo, onEditModuleContent, onEditModuleSettings, onDeleteModule, onInsertModule }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedModule, setDraggedModule] = useState(null);
  // Track which vertical drop zone should be visible (by row index)
  // This can be set by either: 1) row bottom edge hover, or 2) drop zone hover
  // Special value: 'top' for the drop zone at the top of the page
  const [activeVerticalDropZone, setActiveVerticalDropZone] = useState(null);

  const pageRows = pageInfo?.pageRows || [];

  // Index of the row that currently contains the dragged module (if any)
  const draggedRowIndex = useMemo(() => {
    if (!draggedModule) return null;
    const index = pageRows.findIndex((row) => row.pageModules && row.pageModules.some((m) => m.id === draggedModule.id));
    return index === -1 ? null : index;
  }, [draggedModule, pageRows]);

  // Number of modules in the row that currently contains the dragged module.
  // Used for special rules such as allowing adjacent vertical zones when
  // dragging from a two-module row.
  const draggedRowModulesCount = useMemo(() => {
    if (draggedRowIndex === null) return null;
    const row = pageRows[draggedRowIndex];
    if (!row || !row.pageModules) return null;
    return row.pageModules.length;
  }, [draggedRowIndex, pageRows]);

  // Global drag-layer monitor to ensure page drag state resets even if the source unmounts
  const { isDragging: globalIsDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));

  const prevGlobalIsDraggingRef = useRef(false);

  useEffect(() => {
    // When React DnD reports that dragging has stopped but the page is still
    // in drag state, force-reset the page drag flags to avoid "stuck" minimal view.
    if (prevGlobalIsDraggingRef.current && !globalIsDragging && isDragging) {
      setIsDragging(false);
      setDraggedModule(null);
    }
    prevGlobalIsDraggingRef.current = globalIsDragging;
  }, [globalIsDragging, isDragging]);

  // Get theme color the same way as CoursePage (viewCourse)
  const courseTheme = useCourseTheme();

  // RTK Query mutations for module reordering
  const [moveModuleToNewRow] = useMoveModuleToNewRowMutation();
  const [updatePageModule] = useUpdatePageModuleMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const themeColor = useMemo(() => getUserThemeColor(courseTheme?.color), [courseTheme]);

  // Get section and page info for breadcrumbs (same as viewCourse)
  const pageFromViewCourseState = useCoursePageById(pageId);
  const activeSection = useCourseSectionById(pageFromViewCourseState?.sectionId);

  const breadcrumbs = useMemo(() => {
    return [
      <Typo.MediumText key={`section-${activeSection?.name}`}>{activeSection?.name}</Typo.MediumText>,
      <Typo.MediumText key={`page-${pageInfo?.name}`}>{pageInfo?.name}</Typo.MediumText>,
    ];
  }, [activeSection, pageInfo]);

  const handleDragStart = useCallback((module) => {
    // Set state immediately so drop zones show right away (no delay for mobile compatibility)
    setIsDragging(true);
    setDraggedModule(module);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedModule(null);
  }, []);

  const handleDrop = useCallback(
    async (dropData) => {
      // Immediately clear drag state to ensure view returns to normal
      setIsDragging(false);
      setDraggedModule(null);

      try {
        if (dropData.type === 'vertical') {
          // Vertical drop: Move module to a new row at specific position
          // API expects 1-based pageRowPosition
          const pageRowPosition = dropData.targetPosition + 1;

          await moveModuleToNewRow({
            moduleId: dropData.moduleId,
            pageRowPosition: pageRowPosition,
            pageId: pageId,
          }).unwrap();

          enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
        } else if (dropData.type === 'horizontal') {
          // Horizontal drop: Move module to existing row at specific position
          // Need to update ALL modules in the target row with new sortOrder

          const targetRow = pageRows.find((row) => row.id === dropData.targetRowId);
          if (!targetRow) {
            throw new Error('Target row not found');
          }

          // Build new module order for the target row
          const targetModules = [...targetRow.pageModules];

          // Remove dragged module if it's already in this row
          const draggedModuleIndex = targetModules.findIndex((m) => m.id === dropData.moduleId);
          if (draggedModuleIndex !== -1) {
            targetModules.splice(draggedModuleIndex, 1);
          }

          // Insert dragged module at the correct position
          let insertIndex;
          if (dropData.targetPosition === 'left') {
            insertIndex = 0;
          } else if (dropData.targetPosition === 'center') {
            insertIndex = 1; // Between first and second
          } else if (dropData.targetPosition === 'right') {
            insertIndex = targetModules.length; // At the end
          }

          // Create the dragged module object (we need it for the array)
          const draggedModule = draggedModuleIndex !== -1 ? targetRow.pageModules[draggedModuleIndex] : { id: dropData.moduleId }; // If from another row, we only have the ID

          targetModules.splice(insertIndex, 0, draggedModule);

          // Update ALL modules in the target row with their new sortOrder
          // This is critical - we must update all modules, not just the dragged one
          const updatePromises = targetModules.map((module, index) =>
            updatePageModule({
              moduleId: module.id,
              pageId: pageId,
              pageRowId: targetRow.id,
              sortOrder: index + 1, // 1-based sortOrder
            }).unwrap(),
          );

          await Promise.all(updatePromises);

          enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
        }
      } catch (error) {
        console.error('Failed to move module:', error);
        enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      }
    },
    [pageId, pageRows, moveModuleToNewRow, updatePageModule, enqueueSnackbar],
  );

  const renderModule = useCallback(
    (module, props) => {
      return (
        <ModuleWrapper
          key={props.key}
          module={module}
          isEditMode={true}
          pageInDragState={isDragging}
          isDraggedModule={!!draggedModule && draggedModule.id === module.id}
          onEditContent={onEditModuleContent}
          onEditSettings={onEditModuleSettings}
          onDelete={onDeleteModule}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          enableDrag={true}
          themeColor={themeColor}
        >
          <Module module={module} columns={props.$columns} themeColor={themeColor} isEditMode={true} />
        </ModuleWrapper>
      );
    },
    [isDragging, draggedModule, themeColor, onEditModuleContent, onEditModuleSettings, onDeleteModule, handleDragStart, handleDragEnd],
  );

  return (
    <>
      <Page>
        <PageSection align='flex-start' gap='30' size={CONTENT_STANDARD}>
        {/* Breadcrumbs - same as viewCourse for WYSIWYG */}
        <Breadcrumbs separator='›' aria-label='breadcrumb'>
          {breadcrumbs}
        </Breadcrumbs>
        <Flex direction='column' width='100%' justify='center' gap='10'>
          {/* Top drop zone - appears when hovering top edge of first row */}
          {isDragging && (
            <VerticalDropZone
              position={0}
              onDrop={handleDrop}
              isVisible={activeVerticalDropZone === 'top'}
              themeColor={themeColor}
              onHoverChange={(isHovered) => {
                if (isHovered) {
                  // Don't show top vertical zone if dragged module is already in the first row
                  // when that row has only a single module. When dragging from a two-module row,
                  // the adjacent top drop zone should be allowed.
                  if (draggedRowIndex === 0 && draggedRowModulesCount === 1) {
                    return;
                  }
                  setActiveVerticalDropZone('top');
                } else if (activeVerticalDropZone === 'top') {
                  setActiveVerticalDropZone(null);
                }
              }}
            />
          )}

          {/* Empty page state - show when there are no modules */}
          {pageRows.length === 0 && onInsertModule && <ModuleInsertionZone position={0} onInsertModule={onInsertModule} isEmpty={true} />}

          {pageRows.map((pageRow, index) => {
            const isDraggedModuleRow = draggedModule && pageRow.pageModules.some((m) => m.id === draggedModule.id);
            const isLastRow = index === pageRows.length - 1;
            const isFirstRow = index === 0;

            // Check if dragged module is last module on the page
            const draggedModuleIsLast = draggedModule && isLastRow && isDraggedModuleRow;

            return (
              <React.Fragment key={`pageRow-${pageRow.id}`}>
                <DraggableRow
                  pageRow={pageRow}
                  isNotesOpen={false}
                  renderModule={renderModule}
                  isDragging={isDragging}
                  draggedModule={draggedModule}
                  onDrop={handleDrop}
                  themeColor={themeColor}
                  onTopHalfHover={(isHovered) => {
                    if (!isHovered) {
                      return;
                    }

                    // Don't show vertical drop zones directly above/below the dragged module's row
                    // when dragging from a single-module row. When dragging from a two-module row,
                    // adjacent vertical zones are allowed (see DRAG_DROP_ARCHITECTURE.md).
                    if (draggedRowIndex !== null && draggedRowModulesCount === 1) {
                      if (isFirstRow) {
                        // Top zone (position 0) is directly above row 0
                        if (draggedRowIndex === 0) {
                          return;
                        }
                      } else {
                        const verticalIndex = index - 1;
                        if (verticalIndex === draggedRowIndex - 1 || verticalIndex === draggedRowIndex) {
                          return;
                        }
                      }
                    }

                    // Exception: Don't show position 0 if dragging first module

                    setActiveVerticalDropZone(isFirstRow ? 'top' : index - 1);
                  }}
                  onBottomHalfHover={(isHovered) => {
                    if (!isHovered) {
                      return;
                    }

                    // Don't show vertical drop zones directly above/below the dragged module's row
                    // when dragging from a single-module row. When dragging from a two-module row,
                    // adjacent vertical zones are allowed (see DRAG_DROP_ARCHITECTURE.md).
                    if (draggedRowIndex !== null && draggedRowModulesCount === 1) {
                      const verticalIndex = index;
                      if (verticalIndex === draggedRowIndex - 1 || verticalIndex === draggedRowIndex) {
                        return;
                      }
                    }

                    // Exception: Don't show last position if dragging last module
                    if (isLastRow && draggedModuleIsLast) {
                      return;
                    }

                    setActiveVerticalDropZone(index);
                  }}
                />

                {/* Vertical drop zone after each row (except last row) */}
                {/* Shows when hovering bottom half of this row OR when hovering the drop zone itself */}
                {!isLastRow && (
                  <VerticalDropZone
                    position={index + 1}
                    onDrop={handleDrop}
                    isVisible={isDragging && activeVerticalDropZone === index}
                    themeColor={themeColor}
                    onHoverChange={(isHovered) => {
                      if (isHovered) {
                        // Don't show vertical drop zones directly above/below the dragged module's row
                        // when dragging from a single-module row. When dragging from a two-module row,
                        // adjacent vertical zones are allowed (see DRAG_DROP_ARCHITECTURE.md).
                        if (draggedRowIndex !== null && draggedRowModulesCount === 1) {
                          const verticalIndex = index;
                          if (verticalIndex === draggedRowIndex - 1 || verticalIndex === draggedRowIndex) {
                            return;
                          }
                        }

                        // Exception: Don't show last position if dragging last module
                        if (isLastRow && draggedModuleIsLast) {
                          return;
                        }
                        setActiveVerticalDropZone(index);
                      } else if (activeVerticalDropZone === index) {
                        setActiveVerticalDropZone(null);
                      }
                    }}
                  />
                )}

                {/* Original insertion zone for adding new modules */}
                {onInsertModule && !isDragging && <ModuleInsertionZone position={index + 1} onInsertModule={onInsertModule} isEmpty={false} />}
              </React.Fragment>
            );
          })}
        </Flex>
        </PageSection>
      </Page>
    </>
  );
};

export default DraggableCoursePage;
